import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { Close, ArrowForward } from "@material-ui/icons";
import { BRAND } from "src/theme/brandTokens";
import { formatearMonedaCliente } from "src/utils/formatearMonedaCliente";
import { splitMontoMoneda } from "src/utils/splitMontoMonedaCliente";
import { esCobroPorTrabajador } from "src/utils/propuestaItemLabels";
import { BOTON_BASE, BotonPrimario, BotonSecundario } from "./_botones";

// ─── Sub-componentes ────────────────────────────────────────────────────────

// Variante especializada del sticky bar: outline blanco translúcido sobre el
// fondo dark del banner. No se mueve al util porque la paleta sobre dark no
// es reutilizable en otros contextos del portal.
const BotonRechazarToda = ({ onClick, disabled, label }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      ...BOTON_BASE,
      bgcolor: "transparent",
      color: BRAND.color.paper,
      border: `1.5px solid rgba(255, 255, 255, 0.6)`,
      "&:hover:not(:disabled)": {
        borderColor: BRAND.color.paper,
        bgcolor: "rgba(255, 255, 255, 0.08)",
      },
    }}
  >
    <Close fontSize="small" />
    {label}
  </Box>
);

const MontoBloque = ({ monto, sigla }) => (
  <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
    <Box
      sx={{
        fontFamily: BRAND.font.family.display,
        fontSize: BRAND.font.size.h2,
        fontWeight: BRAND.font.weight.black,
        color: BRAND.color.paper,
        lineHeight: BRAND.font.lineHeight.tight,
      }}
    >
      {monto}
    </Box>
    {sigla && (
      <Box
        sx={{
          fontSize: BRAND.font.size.body,
          fontWeight: BRAND.font.weight.bold,
          color: BRAND.color.primary,
          letterSpacing: BRAND.font.letterSpacing.wide,
        }}
      >
        {sigla}
      </Box>
    )}
  </Box>
);

// ─── Componente principal ───────────────────────────────────────────────────

// Los cuatro textos del rechazo masivo cambian de significado según si hay
// servicios que ya quedaron fuera de la mesa: sin ellos, "Rechazar toda" es
// exacto porque `items` es la propuesta completa. Con ellos, la acción ya no
// alcanza a los servicios aceptados en una ronda anterior -el backend ni
// siquiera los aceptaría en el envío- así que los textos tienen que decir
// "lo pendiente" en vez de prometer un rechazo total que no va a ocurrir.
const textosRechazoMasivo = (hayServiciosCerrados) =>
  hayServiciosCerrados
    ? {
        boton: "Rechazar lo pendiente",
        titulo: "Rechazar los servicios pendientes",
        cuerpo:
          "Los servicios que siguen en conversación quedarán marcados como rechazados. Los que ya aceptaste no cambian. Puedes indicar un motivo común (opcional) que se aplicará a todos.",
        confirmar: "Confirmar rechazo",
      }
    : {
        boton: "Rechazar toda",
        titulo: "Rechazar toda la propuesta",
        cuerpo:
          "Todos los servicios de esta propuesta quedarán marcados como rechazados. Puedes indicar un motivo común (opcional) que se aplicará a todos.",
        confirmar: "Confirmar rechazo total",
      };

const StickyActionBar = ({
  items,
  decisions,
  onSubmit,
  onRejectAll,
  submitting,
  hayServiciosCerrados,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [motivoComun, setMotivoComun] = useState("");

  const totalItems = items.length;
  if (totalItems === 0) return null;

  const textos = textosRechazoMasivo(hayServiciosCerrados);

  const aceptados = items.filter(
    (it) => decisions?.[it.id]?.decision === "ACEPTADO",
  );
  const decididos = items.filter((it) => decisions?.[it.id]?.decision).length;
  const todosDecididos = decididos === totalItems;
  const pendientes = totalItems - decididos;

  // Monto agregado por moneda de los items aceptados, en dos acumulados: los
  // planes que se cobran por trabajador (ASESORIA LABORAL Y REMUNERACIONES,
  // PLAN REMUNERACIONES) no comparten base de cálculo con el resto, así que
  // sumarlos junto con los montos fijos daría un total que no corresponde a
  // ningún cobro real. Dentro de cada acumulado, CLP y UF tampoco se mezclan
  // entre sí — no se suman porque son obligaciones distintas.
  const subtotalAceptado = aceptados.reduce(
    (acc, it) => {
      const bucket =
        it.tipoItem === "PLAN" && esCobroPorTrabajador(it.refTipoPlan)
          ? "porTrabajador"
          : "fijo";
      const m = formatearMonedaCliente(it.tipoMoneda) || "CLP";
      acc[bucket][m] =
        (acc[bucket][m] || 0) + Number(it.valorConDescuento || 0);
      return acc;
    },
    { fijo: {}, porTrabajador: {} },
  );
  const monedasAceptadas = Object.entries(subtotalAceptado.fijo);
  const monedasAceptadasPorTrabajador = Object.entries(
    subtotalAceptado.porTrabajador,
  );
  const sinAceptados =
    monedasAceptadas.length === 0 && monedasAceptadasPorTrabajador.length === 0;

  const cerrarDialog = () => {
    setDialogOpen(false);
    setMotivoComun("");
  };

  const handleConfirmRejectAll = () => {
    onRejectAll(motivoComun);
    cerrarDialog();
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          bgcolor: BRAND.color.inkSoft,
          borderTop: `3px solid ${BRAND.color.primary}`,
          fontFamily: BRAND.font.family.base,
          color: BRAND.color.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 2, md: 4 },
            flexWrap: "wrap",
            rowGap: 1.5,
            px: { xs: 3, md: 5 },
            py: { xs: 2, md: 2.5 },
            maxWidth: 1280,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                fontSize: BRAND.font.size.eyebrow,
                color: BRAND.color.grey50,
                textTransform: "uppercase",
                letterSpacing: BRAND.font.letterSpacing.widest,
                fontWeight: BRAND.font.weight.medium,
              }}
            >
              Aceptado · {aceptados.length}{" "}
              {aceptados.length === 1 ? "servicio" : "servicios"}
            </Box>
            {sinAceptados ? (
              <MontoBloque monto="$0" sigla="CLP" />
            ) : (
              <>
                {monedasAceptadas.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "baseline",
                      columnGap: 3,
                      rowGap: 0.5,
                    }}
                  >
                    {monedasAceptadas.map(([m, v]) => {
                      const { monto, sigla } = splitMontoMoneda(v, m);
                      return (
                        <MontoBloque key={m} monto={monto} sigla={sigla} />
                      );
                    })}
                  </Box>
                )}
                {monedasAceptadasPorTrabajador.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "baseline",
                      columnGap: 3,
                      rowGap: 0.5,
                    }}
                  >
                    {monedasAceptadasPorTrabajador.map(([m, v]) => {
                      const { monto, sigla } = splitMontoMoneda(v, m);
                      return (
                        <MontoBloque
                          key={`${m}-trabajador`}
                          monto={monto}
                          sigla={
                            sigla ? `${sigla} / trabajador` : "/ trabajador"
                          }
                        />
                      );
                    })}
                  </Box>
                )}
              </>
            )}
            {!todosDecididos && (
              <Box
                sx={{
                  fontSize: BRAND.font.size.bodySmall,
                  color: BRAND.color.grey50,
                  mt: 0.5,
                }}
              >
                Falta{pendientes !== 1 ? "n" : ""} {pendientes} decisión
                {pendientes !== 1 ? "es" : ""} por tomar antes de firmar
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              rowGap: 1,
            }}
          >
            <BotonRechazarToda
              onClick={() => setDialogOpen(true)}
              disabled={submitting}
              label={textos.boton}
            />
            <BotonPrimario
              onClick={onSubmit}
              disabled={!todosDecididos || submitting}
            >
              {submitting ? "Enviando…" : "Firmar aceptación"}
              {!submitting && <ArrowForward fontSize="small" />}
            </BotonPrimario>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={submitting ? undefined : cerrarDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: BRAND.font.family.display,
            fontWeight: BRAND.font.weight.black,
            color: BRAND.color.inkSoft,
          }}
        >
          {textos.titulo}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              fontFamily: BRAND.font.family.base,
              fontSize: BRAND.font.size.body,
              color: BRAND.color.grey70,
              lineHeight: BRAND.font.lineHeight.body,
              mb: 2,
            }}
          >
            {textos.cuerpo}
          </Box>
          <TextField
            autoFocus
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            variant="outlined"
            placeholder="Motivo opcional"
            value={motivoComun}
            onChange={(e) => setMotivoComun(e.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <BotonSecundario onClick={cerrarDialog} disabled={submitting}>
            Cancelar
          </BotonSecundario>
          <Box
            component="button"
            type="button"
            onClick={handleConfirmRejectAll}
            disabled={submitting}
            sx={{
              ...BOTON_BASE,
              bgcolor: BRAND.color.inkSoft,
              color: BRAND.color.paper,
              border: `1.5px solid ${BRAND.color.inkSoft}`,
              "&:hover:not(:disabled)": {
                bgcolor: BRAND.color.ink,
                borderColor: BRAND.color.ink,
              },
            }}
          >
            {submitting ? "Enviando…" : textos.confirmar}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── PropTypes ──────────────────────────────────────────────────────────────

BotonRechazarToda.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

MontoBloque.propTypes = {
  monto: PropTypes.string.isRequired,
  sigla: PropTypes.string,
};

StickyActionBar.propTypes = {
  items: PropTypes.array.isRequired,
  decisions: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onRejectAll: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  hayServiciosCerrados: PropTypes.bool,
};

StickyActionBar.defaultProps = {
  decisions: {},
  submitting: false,
  hayServiciosCerrados: false,
};

export default StickyActionBar;
